/**
 * Mentions utilities — UI-6 / TD-V2-NOTAS-01.
 *
 * Stores `@user-id` no body texto cru (sem persistência adicional no backend).
 * Frontend parsea + renderiza como link/badge no render. Autocomplete sugere
 * usuários enquanto digita.
 *
 * Decisão de design: usar `user-id` direto (UUID/slug) em vez de display name
 * — IDs são imutáveis (nomes mudam). Render lookup-tier resolve pra nome no
 * momento da exibição. Trade-off: body fica menos legível em raw text.
 */

export interface MentionUser {
  id: string;
  nome: string;
}

/**
 * Encontra o "trigger" de mention ativo na posição do cursor.
 *
 * Trigger: `@` seguido de caracteres não-espaço, com cursor logo após.
 * Retorna `{ start, query }` onde `start` é o índice do `@` e `query` é
 * o que foi digitado depois (pra filtrar lista). Retorna null se cursor
 * não está num trigger ativo.
 *
 * Exemplos:
 *   findMentionTrigger("oi @bo", 6)      → { start: 3, query: 'bo' }
 *   findMentionTrigger("oi @bo aa", 8)   → null (cursor depois do espaço)
 *   findMentionTrigger("@", 1)           → { start: 0, query: '' }
 *   findMentionTrigger("e@mail", 6)      → null (não tem espaço antes)
 */
export function findMentionTrigger(
  text: string,
  cursorPos: number,
): { start: number; query: string } | null {
  if (cursorPos > text.length) return null;
  // Anda pra trás procurando @
  let i = cursorPos - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      // Confirma que antes do @ é início OU espaço/newline
      if (i === 0 || /\s/.test(text[i - 1])) {
        return { start: i, query: text.slice(i + 1, cursorPos) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
}

/**
 * Aplica um match: substitui `@query` (no range start..cursorPos) por
 * `@user-id ` (com espaço pra continuar digitando). Retorna { newText, newCursor }.
 */
export function applyMention(
  text: string,
  start: number,
  cursorPos: number,
  userId: string,
): { newText: string; newCursor: number } {
  const replacement = `@${userId} `;
  const before = text.slice(0, start);
  const after = text.slice(cursorPos);
  const newText = before + replacement + after;
  const newCursor = before.length + replacement.length;
  return { newText, newCursor };
}

/**
 * Filtra users por query (substring case-insensitive sobre nome ou id).
 * Cap em 5 sugestões.
 */
export function filterMentionCandidates(
  users: MentionUser[],
  query: string,
): MentionUser[] {
  const q = query.toLowerCase();
  if (!q) return users.slice(0, 5);
  return users
    .filter((u) => u.nome.toLowerCase().includes(q) || u.id.toLowerCase().includes(q))
    .slice(0, 5);
}

/**
 * Substitui `@user-id` por `@Nome` no body pra display. Mantém o `@`
 * pra deixar claro que é menção. user-ids não-encontrados são preservados
 * (degradam graciosamente).
 *
 * Regex: `@` seguido de `[a-zA-Z0-9-_]+` (matches typical user-ids).
 * NÃO matcha emails (`a@b.com` — `b.com` tem `.` que não está na classe).
 */
export function resolveMentions(body: string, users: MentionUser[]): string {
  const byId = new Map(users.map((u) => [u.id, u.nome]));
  return body.replace(/@([a-zA-Z0-9_-]+)/g, (full, id: string) => {
    const nome = byId.get(id);
    if (nome) return `@${nome}`;
    // A2 (LOTE-FEATURES): um token de USUÁRIO não resolvido (ex.: mencionado
    // que ficou inativo) NUNCA pode mostrar o `@user-<uuid>` cru — degrada pra
    // "@usuário". `@algo` que não é token de usuário fica literal (é só texto).
    return /^user-/.test(id) ? '@usuário' : full;
  });
}

/**
 * B1 (V2-COMERCIAL-AJUDA-UX-QA): segmento de render de um body com menções.
 * `mencao` = token `@user-id` RESOLVIDO pra `@Nome` (pra destacar visualmente);
 * `texto` = resto do body cru (inclui tokens desconhecidos — degradam pra texto).
 */
export interface MentionSegment {
  tipo: 'texto' | 'mencao';
  texto: string;
}

/**
 * Divide o body em segmentos pro render destacar as menções: cada token
 * `@user-id` de um usuário conhecido vira `{ tipo: 'mencao', texto: '@Nome' }`;
 * todo o resto (inclusive `@algo` que não é usuário) fica como texto cru.
 * Mesma regex do resolveMentions — não matcha e-mails.
 */
export function segmentarMencoes(body: string, users: MentionUser[]): MentionSegment[] {
  const byId = new Map(users.map((u) => [u.id, u.nome]));
  const segmentos: MentionSegment[] = [];
  let ultimo = 0;
  for (const match of body.matchAll(/@([a-zA-Z0-9_-]+)/g)) {
    const id = match[1];
    const nome = byId.get(id);
    // A2: token de usuário não resolvido (inativo) vira menção "@usuário" —
    // nunca deixa o uuid cru; `@algo` não-usuário fica no texto.
    const ehUserToken = /^user-/.test(id);
    if (!nome && !ehUserToken) continue;
    const inicio = match.index ?? 0;
    if (inicio > ultimo) segmentos.push({ tipo: 'texto', texto: body.slice(ultimo, inicio) });
    segmentos.push({ tipo: 'mencao', texto: nome ? `@${nome}` : '@usuário' });
    ultimo = inicio + match[0].length;
  }
  if (ultimo < body.length) segmentos.push({ tipo: 'texto', texto: body.slice(ultimo) });
  return segmentos;
}

/**
 * Extrai lista de user-ids mencionados no body. Útil pra disparar
 * notifications no save (R7+).
 */
export function extractMentionedUserIds(body: string, users: MentionUser[]): string[] {
  const known = new Set(users.map((u) => u.id));
  const ids = new Set<string>();
  for (const match of body.matchAll(/@([a-zA-Z0-9_-]+)/g)) {
    const id = match[1];
    if (known.has(id)) ids.add(id);
  }
  return Array.from(ids);
}

/**
 * Emoji shortcuts — substitui `:nome:` por emoji se conhecido. Tabela curta
 * com os mais úteis em contexto de trabalho.
 */
const EMOJI_MAP: Record<string, string> = {
  ':smile:': '😄',
  ':rocket:': '🚀',
  ':thumbsup:': '👍',
  ':+1:': '👍',
  ':thumbsdown:': '👎',
  ':-1:': '👎',
  ':warning:': '⚠️',
  ':check:': '✅',
  ':x:': '❌',
  ':fire:': '🔥',
  ':eyes:': '👀',
  ':lock:': '🔒',
  ':party:': '🎉',
  ':bug:': '🐛',
  ':clock:': '⏰',
  ':star:': '⭐',
};

export function expandEmojis(text: string): string {
  return text.replace(/:[a-z+\-0-9]+:/g, (m) => EMOJI_MAP[m] ?? m);
}
